import { useAppSelector } from 'app/hooks';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import { selectIsEditMode } from 'features/common/editModeSlice';
import { Button, Form, Modal, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError } from 'axios';
import AlertModal from 'components/common/AlertModal';
import ImageUploadControl from 'components/formfields/ImageUploadControl';
import VideoUploadControl from 'components/formfields/VideoUploadControl';
import {
  DISPLAY_TYPE_IMAGE,
  DISPLAY_TYPE_SOURCE,
  DISPLAY_TYPE_TEXT,
  DISPLAY_TYPE_VIDEO,
} from 'features/Wizard/constants';
import wizardService from 'features/Wizard/wizardService';
import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { displayError, handleServiceError, IMAGE_SIZES } from 'utils';
import './style.scss';

export interface WizardDetailModalProps {
  wizardId?: number;
  onClose?: (isDataChanged: boolean) => void;
}

interface FormValues {
  name: string;
  note: string;

  // intro
  intro_flg: boolean;
  intro_display_type: number;

  intro_video_thumb_ja: string | null;
  intro_video_thumb_en: string | null;
  intro_video_thumb_vi: string | null;
  intro_video_thumb_ko: string | null;
  intro_video_thumb_zh_hant: string | null;
  intro_video_thumb_zh_hans: string | null;

  intro_video_ja: string | null;
  intro_video_en: string | null;
  intro_video_vi: string | null;
  intro_video_ko: string | null;
  intro_video_zh_hant: string | null;
  intro_video_zh_hans: string | null;

  intro_image_ja: string | null;
  intro_image_en: string | null;
  intro_image_vi: string | null;
  intro_image_ko: string | null;
  intro_image_zh_hant: string | null;
  intro_image_zh_hans: string | null;

  intro_text_ja: string;
  intro_text_en: string;
  intro_text_vi: string;
  intro_text_ko: string;
  intro_text_zh_hant: string;
  intro_text_zh_hans: string;

  // outtro
  outtro_flg: boolean;
  outtro_display_type: number;

  outtro_message_ja: string | null;
  outtro_message_en: string | null;
  outtro_message_vi: string | null;
  outtro_message_ko: string | null;
  outtro_message_zh_hant: string | null;
  outtro_message_zh_hans: string | null;

  outtro_video_thumb_ja: string | null;
  outtro_video_thumb_en: string | null;
  outtro_video_thumb_vi: string | null;
  outtro_video_thumb_ko: string | null;
  outtro_video_thumb_zh_hant: string | null;
  outtro_video_thumb_zh_hans: string | null;

  outtro_video_ja: string | null;
  outtro_video_en: string | null;
  outtro_video_vi: string | null;
  outtro_video_ko: string | null;
  outtro_video_zh_hant: string | null;
  outtro_video_zh_hans: string | null;

  outtro_image_ja: string | null;
  outtro_image_en: string | null;
  outtro_image_vi: string | null;
  outtro_image_ko: string | null;
  outtro_image_zh_hant: string | null;
  outtro_image_zh_hans: string | null;

  outtro_text_ja: string | null;
  outtro_text_en: string | null;
  outtro_text_vi: string | null;
  outtro_text_ko: string | null;
  outtro_text_zh_hant: string | null;
  outtro_text_zh_hans: string | null;

  // error messages
  warning_required_selection_ja: string | null;
  warning_required_selection_en: string | null;
  warning_required_selection_vi: string | null;
  warning_required_selection_ko: string | null;
  warning_required_selection_zh_hant: string | null;
  warning_required_selection_zh_hans: string | null;
}

export function WizardDetailModal({ wizardId, onClose }: WizardDetailModalProps) {
  const { t } = useTranslation();
  const { showGlobalModal } = useGlobalModal();

  // selectors
  const isEditMode = useAppSelector(selectIsEditMode);

  // yup setup
  const schema = yup.object({
    name: yup.string().required(t('WizardDetailModal.warn_field_required')),

    // intro
    intro_flg: yup.boolean(),
    intro_display_type: yup.number().required(t('WizardDetailModal.warn_field_required')),
    intro_video_thumb_ja: yup
      .string()
      .nullable()
      .when(['intro_flg', 'intro_display_type'], {
        // const flg = Array.isArray(introFlg) ? introFlg[0] : introFlg;
        // const type = Array.isArray(introDisplayType) ? introDisplayType[0] : introDisplayType;

        // if (flg && type === DISPLAY_TYPE_VIDEO) {
        //   return schema.required(t('WizardDetailModal.warn_field_required'));
        // }

        // return schema.notRequired();
        is: (introFlg: boolean, introDisplayType: number) =>
          introFlg === true && introDisplayType === DISPLAY_TYPE_VIDEO,
        then: (s) => s.required(t('WizardDetailModal.warn_field_required')),
        otherwise: (s) => s.notRequired(),
      }),
    intro_video_ja: yup
      .string()
      .nullable()
      .when(['intro_flg', 'intro_display_type'], {
        // const value: number = Array.isArray(introDisplayType) ? introDisplayType[0] : introDisplayType;
        // if (value === DISPLAY_TYPE_VIDEO) {
        //   return schema.required(t('WizardDetailModal.warn_field_required'));
        // }

        // return schema.notRequired();
        is: (introFlg: boolean, introDisplayType: number) =>
          introFlg === true && introDisplayType === DISPLAY_TYPE_VIDEO,
        then: (s) => s.required(t('WizardDetailModal.warn_field_required')),
        otherwise: (s) => s.notRequired(),
      }),
    intro_image_ja: yup
      .string()
      .nullable()
      .when(['intro_flg', 'intro_display_type'], {
        // const value: number = Array.isArray(introDisplayType) ? introDisplayType[0] : introDisplayType;
        // if (value === DISPLAY_TYPE_IMAGE) {
        //   return schema.required(t('WizardDetailModal.warn_field_required'));
        // }

        // return schema.notRequired();
        is: (introFlg: boolean, introDisplayType: number) =>
          introFlg === true && introDisplayType === DISPLAY_TYPE_IMAGE,
        then: (s) => s.required(t('WizardDetailModal.warn_field_required')),
        otherwise: (s) => s.notRequired(),
      }),
    intro_text_ja: yup
      .string()
      .nullable()
      .required(t('WizardDetailModal.warn_field_required'))
      .when(['intro_flg', 'intro_display_type'], {
        // const value: number = Array.isArray(introDisplayType) ? introDisplayType[0] : introDisplayType;
        // if (value === DISPLAY_TYPE_TEXT) {
        //   return schema.required(t('WizardDetailModal.warn_field_required'));
        // }

        // return schema.notRequired();
        is: (introFlg: boolean, introDisplayType: number) =>
          introFlg === true && introDisplayType === DISPLAY_TYPE_TEXT,
        then: (s) => s.required(t('WizardDetailModal.warn_field_required')),
        otherwise: (s) => s.notRequired(),
      }),

    // outtro
    outtro_flg: yup.boolean(),
    outtro_message_ja: yup
      .string()
      .nullable()
      .when('outtro_flg', {
        is: (outtroFlg: boolean) => outtroFlg === true,
        then: (s) => s.required(t('WizardDetailModal.warn_field_required')),
        otherwise: (s) => s.notRequired(),
      }),
    outtro_display_type: yup.number().required(t('WizardDetailModal.warn_field_required')),
    outtro_video_thumb_ja: yup
      .string()
      .nullable()
      .when(['outtro_flg', 'outtro_display_type'], {
        // const value: number = Array.isArray(outtroDisplayType) ? outtroDisplayType[0] : outtroDisplayType;
        // if (value === DISPLAY_TYPE_VIDEO) {
        //   return schema.required(t('WizardDetailModal.warn_field_required'));
        // }

        // return schema.notRequired();
        is: (outtroFlg: boolean, outtroDisplayType: number) =>
          outtroFlg === true && outtroDisplayType === DISPLAY_TYPE_VIDEO,
        then: (s) => s.required(t('WizardDetailModal.warn_field_required')),
        otherwise: (s) => s.notRequired(),
      }),
    outtro_video_ja: yup
      .string()
      .nullable()
      .when(['outtro_flg', 'outtro_display_type'], {
        // const value: number = Array.isArray(outtroDisplayType) ? outtroDisplayType[0] : outtroDisplayType;
        // if (value === DISPLAY_TYPE_VIDEO) {
        //   return schema.required(t('WizardDetailModal.warn_field_required'));
        // }

        // return schema.notRequired();
        is: (outtroFlg: boolean, outtroDisplayType: number) =>
          outtroFlg === true && outtroDisplayType === DISPLAY_TYPE_VIDEO,
        then: (s) => s.required(t('WizardDetailModal.warn_field_required')),
        otherwise: (s) => s.notRequired(),
      }),
    outtro_image_ja: yup
      .string()
      .nullable()
      .when(['outtro_flg', 'outtro_display_type'], {
        // const value: number = Array.isArray(outtroDisplayType) ? outtroDisplayType[0] : outtroDisplayType;
        // if (value === DISPLAY_TYPE_IMAGE) {
        //   return schema.required(t('WizardDetailModal.warn_field_required'));
        // }

        // return schema.notRequired();
        is: (outtroFlg: boolean, outtroDisplayType: number) =>
          outtroFlg === true && outtroDisplayType === DISPLAY_TYPE_IMAGE,
        then: (s) => s.required(t('WizardDetailModal.warn_field_required')),
        otherwise: (s) => s.notRequired(),
      }),
    outtro_text_ja: yup
      .string()
      .nullable()
      .required(t('WizardDetailModal.warn_field_required'))
      .when(['outtro_flg', 'outtro_display_type'], {
        // const value: number = Array.isArray(outtroDisplayType) ? outtroDisplayType[0] : outtroDisplayType;
        // if (value === DISPLAY_TYPE_TEXT) {
        //   return schema.required(t('WizardDetailModal.warn_field_required'));
        // }

        // return schema.notRequired();
        is: (outtroFlg: boolean, outtroDisplayType: number) =>
          outtroFlg === true && outtroDisplayType === DISPLAY_TYPE_TEXT,
        then: (s) => s.required(t('WizardDetailModal.warn_field_required')),
        otherwise: (s) => s.notRequired(),
      }),

    // error messages
    warning_required_selection_ja: yup.string().nullable().required(t('WizardDetailModal.warn_field_required')),
  });

  const {
    register,
    handleSubmit,
    control,
    reset,
    getValues,
    // setValue,
    formState: { isSubmitting, errors },
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      note: '',

      // intro
      intro_flg: true,
      intro_display_type: DISPLAY_TYPE_VIDEO,
      intro_video_thumb_ja: '',
      intro_video_thumb_en: '',
      intro_video_thumb_vi: '',
      intro_video_thumb_ko: '',
      intro_video_thumb_zh_hant: '',
      intro_video_thumb_zh_hans: '',
      intro_video_ja: '',
      intro_video_en: '',
      intro_video_vi: '',
      intro_video_ko: '',
      intro_video_zh_hant: '',
      intro_video_zh_hans: '',
      intro_image_ja: '',
      intro_image_en: '',
      intro_image_vi: '',
      intro_image_ko: '',
      intro_image_zh_hant: '',
      intro_image_zh_hans: '',
      intro_text_ja: '',
      intro_text_en: '',
      intro_text_vi: '',
      intro_text_ko: '',
      intro_text_zh_hant: '',
      intro_text_zh_hans: '',

      // outtro
      outtro_flg: true,
      outtro_display_type: DISPLAY_TYPE_VIDEO,
      outtro_message_ja: '',
      outtro_message_en: '',
      outtro_message_vi: '',
      outtro_message_ko: '',
      outtro_message_zh_hant: '',
      outtro_message_zh_hans: '',
      outtro_video_thumb_ja: '',
      outtro_video_thumb_en: '',
      outtro_video_thumb_vi: '',
      outtro_video_thumb_ko: '',
      outtro_video_thumb_zh_hant: '',
      outtro_video_thumb_zh_hans: '',
      outtro_video_ja: '',
      outtro_video_en: '',
      outtro_video_vi: '',
      outtro_video_ko: '',
      outtro_video_zh_hant: '',
      outtro_video_zh_hans: '',
      outtro_image_ja: '',
      outtro_image_en: '',
      outtro_image_vi: '',
      outtro_image_ko: '',
      outtro_image_zh_hant: '',
      outtro_image_zh_hans: '',
      outtro_text_ja: '',
      outtro_text_en: '',
      outtro_text_vi: '',
      outtro_text_ko: '',
      outtro_text_zh_hant: '',
      outtro_text_zh_hans: '',

      // error messages
      warning_required_selection_ja: '',
      warning_required_selection_en: '',
      warning_required_selection_vi: '',
      warning_required_selection_ko: '',
      warning_required_selection_zh_hant: '',
      warning_required_selection_zh_hans: '',
    },
  });

  // watchers
  const watchIntroDisplayType = Number(
    useWatch({
      control,
      name: 'intro_display_type',
      defaultValue: getValues('intro_display_type'),
    })
  );

  const watchIntroFlg = Boolean(
    useWatch({
      control,
      name: 'intro_flg',
      defaultValue: getValues('intro_flg'),
    })
  );

  const watchOuttroDisplayType = Number(
    useWatch({
      control,
      name: 'outtro_display_type',
      defaultValue: getValues('outtro_display_type'),
    })
  );

  const watchOuttroFlg = Boolean(
    useWatch({
      control,
      name: 'outtro_flg',
      defaultValue: getValues('outtro_flg'),
    })
  );

  // load data
  function getWizardFromWizardId() {
    if (!wizardId) return;

    (async () => {
      try {
        const response = await wizardService.getItem(wizardId);
        reset(response.data);
      } catch (e) {
        if (!handleServiceError(showGlobalModal, e as AxiosError)) {
          displayError(showGlobalModal, e as AxiosError);
        }
      }
    })();
  }

  // side effects
  useEffect(() => {
    getWizardFromWizardId();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // events
  function handleClose(isDataChanged: boolean = false) {
    onClose?.(isDataChanged);
  }

  function onSubmit(data: FormValues) {
    try {
      if (wizardId) {
        // update existing wizard
        (async () => {
          const response = await wizardService.update(wizardId, data);
          showGlobalModal(() => (
            <AlertModal title={t('App.info')} message={response.message} onClose={handleClose.bind(null, true)} />
          ));
        })();
      } else {
        // create new wizard
        (async () => {
          const response = await wizardService.add(data);
          showGlobalModal(() => (
            <AlertModal title={t('App.info')} message={response.message} onClose={handleClose.bind(null, true)} />
          ));
        })();
      }
    } catch (e) {
      if (!handleServiceError(showGlobalModal, e as AxiosError)) {
        displayError(showGlobalModal, e as AxiosError);
      }
    }
  }

  // compose some utils values
  // intro
  const classNameIntroDisplayType = watchIntroFlg ? '' : 'd-none';
  const classNameIntroDisplayVideo = watchIntroFlg && watchIntroDisplayType === DISPLAY_TYPE_VIDEO ? '' : 'd-none';
  const classNameIntroDisplayImage = watchIntroFlg && watchIntroDisplayType === DISPLAY_TYPE_IMAGE ? '' : 'd-none';
  const classNameIntroDisplayText = watchIntroFlg && watchIntroDisplayType === DISPLAY_TYPE_TEXT ? '' : 'd-none';
  const numOfRowSpanIntro = !watchIntroFlg
    ? 1
    : watchIntroDisplayType === DISPLAY_TYPE_VIDEO
    ? 14
    : watchIntroDisplayType === DISPLAY_TYPE_IMAGE
    ? 8
    : 8;

  //outtro
  const classNameOuttroDisplayType = watchOuttroFlg ? '' : 'd-none';
  const classNameOuttroMessage = watchOuttroFlg ? '' : 'd-none';
  const classNameOuttroDisplayVideo = watchOuttroFlg && watchOuttroDisplayType === DISPLAY_TYPE_VIDEO ? '' : 'd-none';
  const classNameOuttroDisplayImage = watchOuttroFlg && watchOuttroDisplayType === DISPLAY_TYPE_IMAGE ? '' : 'd-none';
  const classNameOuttroDisplayText = watchOuttroFlg && watchOuttroDisplayType === DISPLAY_TYPE_TEXT ? '' : 'd-none';
  const numOfRowSpanOuttro = !watchOuttroFlg
    ? 1
    : watchOuttroDisplayType === DISPLAY_TYPE_VIDEO
    ? 20
    : watchOuttroDisplayType === DISPLAY_TYPE_IMAGE
    ? 14
    : 14;

  console.log('errors: ', errors);
  return (
    <Modal size="lg" dialogClassName="wizard-detail-modal" show backdrop="static" onHide={handleClose} centered>
      <Modal.Header closeButton className={isEditMode ? 'reedit-mode-wrapper' : ''}>
        <FeatureTitleBar title={t('WizardDetailModal.head_title')}></FeatureTitleBar>
      </Modal.Header>
      <Modal.Body className={isEditMode ? 'reedit-mode-wrapper' : ''}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="text-end mb-3">
            <Button type="submit" className="btn--dark-green" disabled={isSubmitting}>
              {t('WizardDetailModal.save')}
            </Button>
          </div>
          <Table bordered className="tbl-wizard-detail">
            <tbody>
              <tr>
                <th colSpan={3} rowSpan={1}>
                  {t('WizardDetailModal.wizard_name')}
                </th>
                <td>
                  <Form.Control className="text-center" {...register('name')}></Form.Control>
                  {errors.name && <small className="text-danger d-inline-block mt-1">{errors.name.message}</small>}
                </td>
              </tr>
              {/* INTRO */}
              <tr>
                <th style={{ width: '10%' }} className="intro" colSpan={1} rowSpan={numOfRowSpanIntro}>
                  {t('WizardDetailModal.intro')}
                </th>
                <th style={{ width: '30%' }} className="intro" colSpan={2} rowSpan={1}>
                  {t('WizardDetailModal.active_flg')}
                </th>
                <td>
                  <div className="d-flex justify-content-center">
                    <Form.Check type="switch" label="" size={1} {...register('intro_flg')} />
                  </div>
                </td>
              </tr>
              <tr className={classNameIntroDisplayType}>
                <th className="intro" colSpan={2} rowSpan={1}>
                  {t('WizardDetailModal.display_type')}
                </th>
                <td>
                  <Form.Select {...register('intro_display_type')}>
                    {DISPLAY_TYPE_SOURCE.map((item, index) => (
                      <option className="text-center" key={index} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </Form.Select>
                </td>
              </tr>
              <tr className={classNameIntroDisplayVideo}>
                <th className="intro" style={{ width: '12%' }} colSpan={1} rowSpan={6}>
                  {t('WizardDetailModal.thumb')}
                </th>
                <th className="intro" style={{ width: '18%' }}>
                  {t('WizardDetailModal.japanese')} (※)
                </th>
                <td>
                  <ImageUploadControl
                    name="intro_video_thumb_ja"
                    control={control}
                    minWidth={IMAGE_SIZES.wizard_video_thumb.minWidth}
                    minHeight={IMAGE_SIZES.wizard_video_thumb.minHeight}
                    maxWidth={IMAGE_SIZES.wizard_video_thumb.maxWidth}
                    maxHeight={IMAGE_SIZES.wizard_video_thumb.maxHeight}
                    maxFileSize={IMAGE_SIZES.wizard_video_thumb.maxFileSize}
                  />
                  {errors.intro_video_thumb_ja && (
                    <small className="text-danger d-inline-block mt-1">{errors.intro_video_thumb_ja.message}</small>
                  )}
                </td>
              </tr>
              <tr className={classNameIntroDisplayVideo}>
                <th className="intro">{t('WizardDetailModal.english')}</th>
                <td>
                  <ImageUploadControl
                    name="intro_video_thumb_en"
                    control={control}
                    minWidth={IMAGE_SIZES.wizard_video_thumb.minWidth}
                    minHeight={IMAGE_SIZES.wizard_video_thumb.minHeight}
                    maxWidth={IMAGE_SIZES.wizard_video_thumb.maxWidth}
                    maxHeight={IMAGE_SIZES.wizard_video_thumb.maxHeight}
                    maxFileSize={IMAGE_SIZES.wizard_video_thumb.maxFileSize}
                  />
                </td>
              </tr>
              <tr className={classNameIntroDisplayVideo}>
                <th className="intro">{t('WizardDetailModal.simplified_chinese')}</th>
                <td>
                  <ImageUploadControl
                    name="intro_video_thumb_zh_hans"
                    control={control}
                    minWidth={IMAGE_SIZES.wizard_video_thumb.minWidth}
                    minHeight={IMAGE_SIZES.wizard_video_thumb.minHeight}
                    maxWidth={IMAGE_SIZES.wizard_video_thumb.maxWidth}
                    maxHeight={IMAGE_SIZES.wizard_video_thumb.maxHeight}
                    maxFileSize={IMAGE_SIZES.wizard_video_thumb.maxFileSize}
                  />
                </td>
              </tr>
              <tr className={classNameIntroDisplayVideo}>
                <th className="intro">{t('WizardDetailModal.transitional_chinese')}</th>
                <td>
                  <ImageUploadControl
                    name="intro_video_thumb_zh_hant"
                    control={control}
                    minWidth={IMAGE_SIZES.wizard_video_thumb.minWidth}
                    minHeight={IMAGE_SIZES.wizard_video_thumb.minHeight}
                    maxWidth={IMAGE_SIZES.wizard_video_thumb.maxWidth}
                    maxHeight={IMAGE_SIZES.wizard_video_thumb.maxHeight}
                    maxFileSize={IMAGE_SIZES.wizard_video_thumb.maxFileSize}
                  />
                </td>
              </tr>
              <tr className={classNameIntroDisplayVideo}>
                <th className="intro">{t('WizardDetailModal.korean')}</th>
                <td>
                  <ImageUploadControl
                    name="intro_video_thumb_ko"
                    control={control}
                    minWidth={IMAGE_SIZES.wizard_video_thumb.minWidth}
                    minHeight={IMAGE_SIZES.wizard_video_thumb.minHeight}
                    maxWidth={IMAGE_SIZES.wizard_video_thumb.maxWidth}
                    maxHeight={IMAGE_SIZES.wizard_video_thumb.maxHeight}
                    maxFileSize={IMAGE_SIZES.wizard_video_thumb.maxFileSize}
                  />
                </td>
              </tr>
              <tr className={classNameIntroDisplayVideo}>
                <th className="intro">{t('WizardDetailModal.vietnamese')}</th>
                <td>
                  <ImageUploadControl
                    name="intro_video_thumb_vi"
                    control={control}
                    minWidth={IMAGE_SIZES.wizard_video_thumb.minWidth}
                    minHeight={IMAGE_SIZES.wizard_video_thumb.minHeight}
                    maxWidth={IMAGE_SIZES.wizard_video_thumb.maxWidth}
                    maxHeight={IMAGE_SIZES.wizard_video_thumb.maxHeight}
                    maxFileSize={IMAGE_SIZES.wizard_video_thumb.maxFileSize}
                  />
                </td>
              </tr>
              <tr className={classNameIntroDisplayVideo}>
                <th className="intro" colSpan={1} rowSpan={6}>
                  {t('WizardDetailModal.video')}
                </th>
                <th className="intro">{t('WizardDetailModal.japanese')} (※)</th>
                <td>
                  <VideoUploadControl name="intro_video_ja" control={control} maxFileSize={0} />
                  {errors.intro_video_ja && (
                    <small className="text-danger d-inline-block mt-1">{errors.intro_video_ja.message}</small>
                  )}
                </td>
              </tr>
              <tr className={classNameIntroDisplayVideo}>
                <th className="intro">{t('WizardDetailModal.english')}</th>
                <td>
                  <VideoUploadControl name="intro_video_en" control={control} maxFileSize={0} />
                </td>
              </tr>
              <tr className={classNameIntroDisplayVideo}>
                <th className="intro">{t('WizardDetailModal.simplified_chinese')}</th>
                <td>
                  <VideoUploadControl name="intro_video_zh_hans" control={control} maxFileSize={0} />
                </td>
              </tr>
              <tr className={classNameIntroDisplayVideo}>
                <th className="intro">{t('WizardDetailModal.transitional_chinese')}</th>
                <td>
                  <VideoUploadControl name="intro_video_zh_hant" control={control} maxFileSize={0} />
                </td>
              </tr>
              <tr className={classNameIntroDisplayVideo}>
                <th className="intro">{t('WizardDetailModal.korean')}</th>
                <td>
                  <VideoUploadControl name="intro_video_ko" control={control} maxFileSize={0} />
                </td>
              </tr>
              <tr className={classNameIntroDisplayVideo}>
                <th className="intro">{t('WizardDetailModal.vietnamese')}</th>
                <td>
                  <VideoUploadControl name="intro_video_vi" control={control} maxFileSize={0} />
                </td>
              </tr>

              {/* intro image */}
              <tr className={classNameIntroDisplayImage}>
                <th className="intro" colSpan={1} rowSpan={6}>
                  {t('WizardDetailModal.image')}
                </th>
                <th className="intro">{t('WizardDetailModal.japanese')} (※)</th>
                <td>
                  <ImageUploadControl
                    name="intro_image_ja"
                    control={control}
                    minWidth={IMAGE_SIZES.wizard_intro_image.minWidth}
                    minHeight={IMAGE_SIZES.wizard_intro_image.minHeight}
                    maxWidth={IMAGE_SIZES.wizard_intro_image.maxWidth}
                    maxHeight={IMAGE_SIZES.wizard_intro_image.maxHeight}
                    maxFileSize={IMAGE_SIZES.wizard_intro_image.maxFileSize}
                  />
                  {errors.intro_image_ja && (
                    <small className="text-danger d-inline-block mt-1">{errors.intro_image_ja.message}</small>
                  )}
                </td>
              </tr>
              <tr className={classNameIntroDisplayImage}>
                <th className="intro">{t('WizardDetailModal.english')}</th>
                <td>
                  <ImageUploadControl
                    name="intro_image_en"
                    control={control}
                    minWidth={IMAGE_SIZES.wizard_intro_image.minWidth}
                    minHeight={IMAGE_SIZES.wizard_intro_image.minHeight}
                    maxWidth={IMAGE_SIZES.wizard_intro_image.maxWidth}
                    maxHeight={IMAGE_SIZES.wizard_intro_image.maxHeight}
                    maxFileSize={IMAGE_SIZES.wizard_intro_image.maxFileSize}
                  />
                </td>
              </tr>
              <tr className={classNameIntroDisplayImage}>
                <th className="intro">{t('WizardDetailModal.simplified_chinese')}</th>
                <td>
                  <ImageUploadControl
                    name="intro_image_zh_hans"
                    control={control}
                    minWidth={IMAGE_SIZES.wizard_intro_image.minWidth}
                    minHeight={IMAGE_SIZES.wizard_intro_image.minHeight}
                    maxWidth={IMAGE_SIZES.wizard_intro_image.maxWidth}
                    maxHeight={IMAGE_SIZES.wizard_intro_image.maxHeight}
                    maxFileSize={IMAGE_SIZES.wizard_intro_image.maxFileSize}
                  />
                </td>
              </tr>
              <tr className={classNameIntroDisplayImage}>
                <th className="intro">{t('WizardDetailModal.transitional_chinese')}</th>
                <td>
                  <ImageUploadControl
                    name="intro_image_zh_hant"
                    control={control}
                    minWidth={IMAGE_SIZES.wizard_intro_image.minWidth}
                    minHeight={IMAGE_SIZES.wizard_intro_image.minHeight}
                    maxWidth={IMAGE_SIZES.wizard_intro_image.maxWidth}
                    maxHeight={IMAGE_SIZES.wizard_intro_image.maxHeight}
                    maxFileSize={IMAGE_SIZES.wizard_intro_image.maxFileSize}
                  />
                </td>
              </tr>
              <tr className={classNameIntroDisplayImage}>
                <th className="intro">{t('WizardDetailModal.korean')}</th>
                <td>
                  <ImageUploadControl
                    name="intro_image_ko"
                    control={control}
                    minWidth={IMAGE_SIZES.wizard_intro_image.minWidth}
                    minHeight={IMAGE_SIZES.wizard_intro_image.minHeight}
                    maxWidth={IMAGE_SIZES.wizard_intro_image.maxWidth}
                    maxHeight={IMAGE_SIZES.wizard_intro_image.maxHeight}
                    maxFileSize={IMAGE_SIZES.wizard_intro_image.maxFileSize}
                  />
                </td>
              </tr>
              <tr className={classNameIntroDisplayImage}>
                <th className="intro">{t('WizardDetailModal.vietnamese')}</th>
                <td>
                  <ImageUploadControl
                    name="intro_image_vi"
                    control={control}
                    minWidth={IMAGE_SIZES.wizard_intro_image.minWidth}
                    minHeight={IMAGE_SIZES.wizard_intro_image.minHeight}
                    maxWidth={IMAGE_SIZES.wizard_intro_image.maxWidth}
                    maxHeight={IMAGE_SIZES.wizard_intro_image.maxHeight}
                    maxFileSize={IMAGE_SIZES.wizard_intro_image.maxFileSize}
                  />
                </td>
              </tr>

              {/* intro text */}
              <tr className={classNameIntroDisplayText}>
                <th className="intro" colSpan={1} rowSpan={6}>
                  {t('WizardDetailModal.text')}
                </th>
                <th className="intro">{t('WizardDetailModal.japanese')} (※)</th>
                <td>
                  <Form.Control as="textarea" rows={2} {...register('intro_text_ja')}></Form.Control>
                  {errors.intro_text_ja && (
                    <small className="text-danger d-inline-block mt-1">{errors.intro_text_ja.message}</small>
                  )}
                </td>
              </tr>
              <tr className={classNameIntroDisplayText}>
                <th className="intro">{t('WizardDetailModal.english')}</th>
                <td>
                  <Form.Control as="textarea" rows={2} {...register('intro_text_en')}></Form.Control>
                </td>
              </tr>
              <tr className={classNameIntroDisplayText}>
                <th className="intro">{t('WizardDetailModal.simplified_chinese')}</th>
                <td>
                  <Form.Control as="textarea" rows={2} {...register('intro_text_zh_hans')}></Form.Control>
                </td>
              </tr>
              <tr className={classNameIntroDisplayText}>
                <th className="intro">{t('WizardDetailModal.transitional_chinese')}</th>
                <td>
                  <Form.Control as="textarea" rows={2} {...register('intro_text_zh_hant')}></Form.Control>
                </td>
              </tr>
              <tr className={classNameIntroDisplayText}>
                <th className="intro">{t('WizardDetailModal.korean')}</th>
                <td>
                  <Form.Control as="textarea" rows={2} {...register('intro_text_ko')}></Form.Control>
                </td>
              </tr>
              <tr className={classNameIntroDisplayText}>
                <th className="intro">{t('WizardDetailModal.vietnamese')}</th>
                <td>
                  <Form.Control as="textarea" rows={2} {...register('intro_text_vi')}></Form.Control>
                </td>
              </tr>

              {/* OUTTRO */}
              <tr>
                <th style={{ width: '10%' }} className="outtro" colSpan={1} rowSpan={numOfRowSpanOuttro}>
                  {t('WizardDetailModal.outtro')}
                </th>
                <th style={{ width: '30%' }} className="outtro" colSpan={2} rowSpan={1}>
                  {t('WizardDetailModal.active_flg')}
                </th>
                <td>
                  <div className="d-flex justify-content-center">
                    <Form.Check type="switch" label="" size={1} {...register('outtro_flg')} />
                  </div>
                </td>
              </tr>

              {/* outtro message */}
              <tr className={classNameOuttroMessage}>
                <th className="outtro" colSpan={1} rowSpan={6}>
                  {t('WizardDetailModal.outtro_message')}
                </th>
                <th className="outtro">{t('WizardDetailModal.japanese')} (※)</th>
                <td>
                  <Form.Control as="textarea" rows={2} {...register('outtro_message_ja')}></Form.Control>
                  {errors.outtro_message_ja && (
                    <small className="text-danger d-inline-block mt-1">{errors.outtro_message_ja.message}</small>
                  )}
                </td>
              </tr>
              <tr className={classNameOuttroMessage}>
                <th className="outtro">{t('WizardDetailModal.english')}</th>
                <td>
                  <Form.Control as="textarea" rows={2} {...register('outtro_message_en')}></Form.Control>
                </td>
              </tr>
              <tr className={classNameOuttroMessage}>
                <th className="outtro">{t('WizardDetailModal.simplified_chinese')}</th>
                <td>
                  <Form.Control as="textarea" rows={2} {...register('outtro_message_zh_hans')}></Form.Control>
                </td>
              </tr>
              <tr className={classNameOuttroMessage}>
                <th className="outtro">{t('WizardDetailModal.transitional_chinese')}</th>
                <td>
                  <Form.Control as="textarea" rows={2} {...register('outtro_message_zh_hant')}></Form.Control>
                </td>
              </tr>
              <tr className={classNameOuttroMessage}>
                <th className="outtro">{t('WizardDetailModal.korean')}</th>
                <td>
                  <Form.Control as="textarea" rows={2} {...register('outtro_message_ko')}></Form.Control>
                </td>
              </tr>
              <tr className={classNameOuttroMessage}>
                <th className="outtro">{t('WizardDetailModal.vietnamese')}</th>
                <td>
                  <Form.Control as="textarea" rows={2} {...register('outtro_message_vi')}></Form.Control>
                </td>
              </tr>

              <tr className={classNameOuttroDisplayType}>
                <th className="outtro" colSpan={2} rowSpan={1}>
                  {t('WizardDetailModal.display_type')}
                </th>
                <td>
                  <Form.Select {...register('outtro_display_type')}>
                    {DISPLAY_TYPE_SOURCE.map((item, index) => (
                      <option className="text-center" key={index} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </Form.Select>
                </td>
              </tr>

              {/* outtro thumb */}
              <tr className={classNameOuttroDisplayVideo}>
                <th className="outtro" style={{ width: '12%' }} colSpan={1} rowSpan={6}>
                  {t('WizardDetailModal.thumb')}
                </th>
                <th className="outtro" style={{ width: '18%' }}>
                  {t('WizardDetailModal.japanese')} (※)
                </th>
                <td>
                  <ImageUploadControl
                    name="outtro_video_thumb_ja"
                    control={control}
                    minWidth={IMAGE_SIZES.wizard_video_thumb.minWidth}
                    minHeight={IMAGE_SIZES.wizard_video_thumb.minHeight}
                    maxWidth={IMAGE_SIZES.wizard_video_thumb.maxWidth}
                    maxHeight={IMAGE_SIZES.wizard_video_thumb.maxHeight}
                    maxFileSize={IMAGE_SIZES.wizard_video_thumb.maxFileSize}
                  />
                  {errors.outtro_video_thumb_ja && (
                    <small className="text-danger d-inline-block mt-1">{errors.outtro_video_thumb_ja.message}</small>
                  )}
                </td>
              </tr>
              <tr className={classNameOuttroDisplayVideo}>
                <th className="outtro">{t('WizardDetailModal.english')}</th>
                <td>
                  <ImageUploadControl
                    name="outtro_video_thumb_en"
                    control={control}
                    minWidth={IMAGE_SIZES.wizard_video_thumb.minWidth}
                    minHeight={IMAGE_SIZES.wizard_video_thumb.minHeight}
                    maxWidth={IMAGE_SIZES.wizard_video_thumb.maxWidth}
                    maxHeight={IMAGE_SIZES.wizard_video_thumb.maxHeight}
                    maxFileSize={IMAGE_SIZES.wizard_video_thumb.maxFileSize}
                  />
                </td>
              </tr>
              <tr className={classNameOuttroDisplayVideo}>
                <th className="outtro">{t('WizardDetailModal.simplified_chinese')}</th>
                <td>
                  <ImageUploadControl
                    name="outtro_video_thumb_zh_hans"
                    control={control}
                    minWidth={IMAGE_SIZES.wizard_video_thumb.minWidth}
                    minHeight={IMAGE_SIZES.wizard_video_thumb.minHeight}
                    maxWidth={IMAGE_SIZES.wizard_video_thumb.maxWidth}
                    maxHeight={IMAGE_SIZES.wizard_video_thumb.maxHeight}
                    maxFileSize={IMAGE_SIZES.wizard_video_thumb.maxFileSize}
                  />
                </td>
              </tr>
              <tr className={classNameOuttroDisplayVideo}>
                <th className="outtro">{t('WizardDetailModal.transitional_chinese')}</th>
                <td>
                  <ImageUploadControl
                    name="outtro_video_thumb_zh_hant"
                    control={control}
                    minWidth={IMAGE_SIZES.wizard_video_thumb.minWidth}
                    minHeight={IMAGE_SIZES.wizard_video_thumb.minHeight}
                    maxWidth={IMAGE_SIZES.wizard_video_thumb.maxWidth}
                    maxHeight={IMAGE_SIZES.wizard_video_thumb.maxHeight}
                    maxFileSize={IMAGE_SIZES.wizard_video_thumb.maxFileSize}
                  />
                </td>
              </tr>
              <tr className={classNameOuttroDisplayVideo}>
                <th className="outtro">{t('WizardDetailModal.korean')}</th>
                <td>
                  <ImageUploadControl
                    name="outtro_video_thumb_ko"
                    control={control}
                    minWidth={IMAGE_SIZES.wizard_video_thumb.minWidth}
                    minHeight={IMAGE_SIZES.wizard_video_thumb.minHeight}
                    maxWidth={IMAGE_SIZES.wizard_video_thumb.maxWidth}
                    maxHeight={IMAGE_SIZES.wizard_video_thumb.maxHeight}
                    maxFileSize={IMAGE_SIZES.wizard_video_thumb.maxFileSize}
                  />
                </td>
              </tr>
              <tr className={classNameOuttroDisplayVideo}>
                <th className="outtro">{t('WizardDetailModal.vietnamese')}</th>
                <td>
                  <ImageUploadControl
                    name="outtro_video_thumb_vi"
                    control={control}
                    minWidth={IMAGE_SIZES.wizard_video_thumb.minWidth}
                    minHeight={IMAGE_SIZES.wizard_video_thumb.minHeight}
                    maxWidth={IMAGE_SIZES.wizard_video_thumb.maxWidth}
                    maxHeight={IMAGE_SIZES.wizard_video_thumb.maxHeight}
                    maxFileSize={IMAGE_SIZES.wizard_video_thumb.maxFileSize}
                  />
                </td>
              </tr>

              {/* outtro video */}
              <tr className={classNameOuttroDisplayVideo}>
                <th className="outtro" colSpan={1} rowSpan={6}>
                  {t('WizardDetailModal.video')}
                </th>
                <th className="outtro">{t('WizardDetailModal.japanese')} (※)</th>
                <td>
                  <VideoUploadControl name="outtro_video_ja" control={control} maxFileSize={0} />
                  {errors.outtro_video_ja && (
                    <small className="text-danger d-inline-block mt-1">{errors.outtro_video_ja.message}</small>
                  )}
                </td>
              </tr>
              <tr className={classNameOuttroDisplayVideo}>
                <th className="outtro">{t('WizardDetailModal.english')}</th>
                <td>
                  <VideoUploadControl name="outtro_video_en" control={control} maxFileSize={0} />
                </td>
              </tr>
              <tr className={classNameOuttroDisplayVideo}>
                <th className="outtro">{t('WizardDetailModal.simplified_chinese')}</th>
                <td>
                  <VideoUploadControl name="outtro_video_zh_hans" control={control} maxFileSize={0} />
                </td>
              </tr>
              <tr className={classNameOuttroDisplayVideo}>
                <th className="outtro">{t('WizardDetailModal.transitional_chinese')}</th>
                <td>
                  <VideoUploadControl name="outtro_video_zh_hant" control={control} maxFileSize={0} />
                </td>
              </tr>
              <tr className={classNameOuttroDisplayVideo}>
                <th className="outtro">{t('WizardDetailModal.korean')}</th>
                <td>
                  <VideoUploadControl name="outtro_video_ko" control={control} maxFileSize={0} />
                </td>
              </tr>
              <tr className={classNameOuttroDisplayVideo}>
                <th className="outtro">{t('WizardDetailModal.vietnamese')}</th>
                <td>
                  <VideoUploadControl name="outtro_video_vi" control={control} maxFileSize={0} />
                </td>
              </tr>

              {/* outtro image */}
              <tr className={classNameOuttroDisplayImage}>
                <th className="outtro" colSpan={1} rowSpan={6}>
                  {t('WizardDetailModal.image')}
                </th>
                <th className="outtro">{t('WizardDetailModal.japanese')} (※)</th>
                <td>
                  <ImageUploadControl
                    name="outtro_image_ja"
                    control={control}
                    minWidth={IMAGE_SIZES.wizard_outtro_image.minWidth}
                    minHeight={IMAGE_SIZES.wizard_outtro_image.minHeight}
                    maxWidth={IMAGE_SIZES.wizard_outtro_image.maxWidth}
                    maxHeight={IMAGE_SIZES.wizard_outtro_image.maxHeight}
                    maxFileSize={IMAGE_SIZES.wizard_outtro_image.maxFileSize}
                  />
                  {errors.outtro_image_ja && (
                    <small className="text-danger d-inline-block mt-1">{errors.outtro_image_ja.message}</small>
                  )}
                </td>
              </tr>
              <tr className={classNameOuttroDisplayImage}>
                <th className="outtro">{t('WizardDetailModal.english')}</th>
                <td>
                  <ImageUploadControl
                    name="outtro_image_en"
                    control={control}
                    minWidth={IMAGE_SIZES.wizard_outtro_image.minWidth}
                    minHeight={IMAGE_SIZES.wizard_outtro_image.minHeight}
                    maxWidth={IMAGE_SIZES.wizard_outtro_image.maxWidth}
                    maxHeight={IMAGE_SIZES.wizard_outtro_image.maxHeight}
                    maxFileSize={IMAGE_SIZES.wizard_outtro_image.maxFileSize}
                  />
                </td>
              </tr>
              <tr className={classNameOuttroDisplayImage}>
                <th className="outtro">{t('WizardDetailModal.simplified_chinese')}</th>
                <td>
                  <ImageUploadControl
                    name="outtro_image_zh_hans"
                    control={control}
                    minWidth={IMAGE_SIZES.wizard_outtro_image.minWidth}
                    minHeight={IMAGE_SIZES.wizard_outtro_image.minHeight}
                    maxWidth={IMAGE_SIZES.wizard_outtro_image.maxWidth}
                    maxHeight={IMAGE_SIZES.wizard_outtro_image.maxHeight}
                    maxFileSize={IMAGE_SIZES.wizard_outtro_image.maxFileSize}
                  />
                </td>
              </tr>
              <tr className={classNameOuttroDisplayImage}>
                <th className="outtro">{t('WizardDetailModal.transitional_chinese')}</th>
                <td>
                  <ImageUploadControl
                    name="outtro_image_zh_hant"
                    control={control}
                    minWidth={IMAGE_SIZES.wizard_outtro_image.minWidth}
                    minHeight={IMAGE_SIZES.wizard_outtro_image.minHeight}
                    maxWidth={IMAGE_SIZES.wizard_outtro_image.maxWidth}
                    maxHeight={IMAGE_SIZES.wizard_outtro_image.maxHeight}
                    maxFileSize={IMAGE_SIZES.wizard_outtro_image.maxFileSize}
                  />
                </td>
              </tr>
              <tr className={classNameOuttroDisplayImage}>
                <th className="outtro">{t('WizardDetailModal.korean')}</th>
                <td>
                  <ImageUploadControl
                    name="outtro_image_ko"
                    control={control}
                    minWidth={IMAGE_SIZES.wizard_outtro_image.minWidth}
                    minHeight={IMAGE_SIZES.wizard_outtro_image.minHeight}
                    maxWidth={IMAGE_SIZES.wizard_outtro_image.maxWidth}
                    maxHeight={IMAGE_SIZES.wizard_outtro_image.maxHeight}
                    maxFileSize={IMAGE_SIZES.wizard_outtro_image.maxFileSize}
                  />
                </td>
              </tr>
              <tr className={classNameOuttroDisplayImage}>
                <th className="outtro">{t('WizardDetailModal.vietnamese')}</th>
                <td>
                  <ImageUploadControl
                    name="outtro_image_vi"
                    control={control}
                    minWidth={IMAGE_SIZES.wizard_outtro_image.minWidth}
                    minHeight={IMAGE_SIZES.wizard_outtro_image.minHeight}
                    maxWidth={IMAGE_SIZES.wizard_outtro_image.maxWidth}
                    maxHeight={IMAGE_SIZES.wizard_outtro_image.maxHeight}
                    maxFileSize={IMAGE_SIZES.wizard_outtro_image.maxFileSize}
                  />
                </td>
              </tr>

              {/* outtro text */}
              <tr className={classNameOuttroDisplayText}>
                <th className="outtro" colSpan={1} rowSpan={6}>
                  {t('WizardDetailModal.text')}
                </th>
                <th className="outtro">{t('WizardDetailModal.japanese')} (※)</th>
                <td>
                  <Form.Control as="textarea" rows={2} {...register('outtro_text_ja')}></Form.Control>
                  {errors.outtro_text_ja && (
                    <small className="text-danger d-inline-block mt-1">{errors.outtro_text_ja.message}</small>
                  )}
                </td>
              </tr>
              <tr className={classNameOuttroDisplayText}>
                <th className="outtro">{t('WizardDetailModal.english')}</th>
                <td>
                  <Form.Control as="textarea" rows={2} {...register('outtro_text_en')}></Form.Control>
                </td>
              </tr>
              <tr className={classNameOuttroDisplayText}>
                <th className="outtro">{t('WizardDetailModal.simplified_chinese')}</th>
                <td>
                  <Form.Control as="textarea" rows={2} {...register('outtro_text_zh_hans')}></Form.Control>
                </td>
              </tr>
              <tr className={classNameOuttroDisplayText}>
                <th className="outtro">{t('WizardDetailModal.transitional_chinese')}</th>
                <td>
                  <Form.Control as="textarea" rows={2} {...register('outtro_text_zh_hant')}></Form.Control>
                </td>
              </tr>
              <tr className={classNameOuttroDisplayText}>
                <th className="outtro">{t('WizardDetailModal.korean')}</th>
                <td>
                  <Form.Control as="textarea" rows={2} {...register('outtro_text_ko')}></Form.Control>
                </td>
              </tr>
              <tr className={classNameOuttroDisplayText}>
                <th className="outtro">{t('WizardDetailModal.vietnamese')}</th>
                <td>
                  <Form.Control as="textarea" rows={2} {...register('outtro_text_vi')}></Form.Control>
                </td>
              </tr>

              {/* error messages */}
              <tr>
                <th colSpan={1} rowSpan={6}>
                  {t('WizardDetailModal.error_message')}
                </th>
                <th colSpan={1} rowSpan={6}>
                  {t('WizardDetailModal.warning_required_selection')}
                </th>
                <th>{t('WizardDetailModal.japanese')} (※)</th>
                <td>
                  <Form.Control as="textarea" rows={2} {...register('warning_required_selection_ja')}></Form.Control>
                  {errors.warning_required_selection_ja && (
                    <small className="text-danger d-inline-block mt-1">
                      {errors.warning_required_selection_ja.message}
                    </small>
                  )}
                </td>
              </tr>
              <tr>
                <th>{t('WizardDetailModal.english')}</th>
                <td>
                  <Form.Control as="textarea" rows={2} {...register('warning_required_selection_en')}></Form.Control>
                </td>
              </tr>
              <tr>
                <th>{t('WizardDetailModal.simplified_chinese')}</th>
                <td>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    {...register('warning_required_selection_zh_hans')}
                  ></Form.Control>
                </td>
              </tr>
              <tr>
                <th>{t('WizardDetailModal.transitional_chinese')}</th>
                <td>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    {...register('warning_required_selection_zh_hant')}
                  ></Form.Control>
                </td>
              </tr>
              <tr>
                <th>{t('WizardDetailModal.korean')}</th>
                <td>
                  <Form.Control as="textarea" rows={2} {...register('warning_required_selection_ko')}></Form.Control>
                </td>
              </tr>
              <tr>
                <th>{t('WizardDetailModal.vietnamese')}</th>
                <td>
                  <Form.Control as="textarea" rows={2} {...register('warning_required_selection_vi')}></Form.Control>
                </td>
              </tr>
              <tr>
                <th colSpan={3}>{t('WizardDetailModal.note')}</th>
                <td>
                  <Form.Control as="textarea" rows={2} {...register('note')}></Form.Control>
                </td>
              </tr>
            </tbody>
          </Table>
        </form>
      </Modal.Body>
    </Modal>
  );
}
